/**
 * Reveal-on-scroll: fades/slides any element carrying the `reveal`
 * class into place the first time it scrolls into view, then stops
 * watching it (scrolling back up/down never re-triggers it). See
 * assets/reu-reveal-on-scroll.css for what "visible" actually looks
 * like (opacity/transform/direction/stagger delay via --reveal-delay)
 * - this file only ever decides WHEN.
 *
 * Delegated (one shared observer for the whole page) rather than a
 * per-element custom element wrapping each target: this class gets
 * applied directly onto many different pre-existing elements across
 * the site (product cards, collection cards, editorial columns, blog
 * cards) - wrapping each one in a new element risks breaking whatever
 * grid/flex layout already treats it as a direct child, since only a
 * direct child of a grid/flex container participates in that
 * container's own layout algorithm. Loaded once, globally, from
 * layout/theme.liquid (paired with the CSS), rather than per-section,
 * so a section just adds the `reveal` class - no script tag to add or
 * remember.
 *
 * A MutationObserver keeps watching for `.reveal` elements added later
 * (infinite scroll, a re-rendered section), so content added after the
 * fact still gets observed.
 */
(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** @type {IntersectionObserver | null} */
  let observer = null;

  if (!prefersReducedMotion) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer?.unobserve(entry.target);
          }
        }
      },
      // Triggers slightly before the element's bottom edge would
      // naturally enter the viewport, so it doesn't feel late.
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
  }

  /**
   * @param {Node} node
   * @returns {Element[]} node itself (if it matches) plus any matching descendants
   */
  const collectReveals = (node) => {
    if (!(node instanceof Element)) return [];
    const matches = node.matches('.reveal') ? [node] : [];
    matches.push(...node.querySelectorAll('.reveal'));
    return matches;
  };

  /**
   * @param {Element[]} elements
   */
  const watch = (elements) => {
    for (const element of elements) {
      if (element.classList.contains('is-visible')) continue;

      // The CSS already forces full visibility with no animation under
      // reduced motion - nothing worth watching for, so just flip the
      // class directly rather than spin up an observer nobody needs.
      if (prefersReducedMotion) {
        element.classList.add('is-visible');
      } else {
        observer?.observe(element);
      }
    }
  };

  watch(collectReveals(document.body));

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        watch(collectReveals(node));
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
